import { defineStore } from "pinia"
import { ref, inject, computed } from "vue"
import { useFetchWrapper } from "@core/composables"
import { Building, Source, UsageProfile } from "@types"
import type { Services } from "@services"
import type { ParcelLocationMultiPolygon } from "@core/services/building.service"
import { METER_TYPES_LIST } from "@core/constants"
import { useSourceStore } from "@core/stores/source.store"

/**
 * Durée de validité du cache des bâtiments par entreprise (5 minutes)
 */
const BUILDINGS_BY_COMPANY_CACHE_TTL_MS = 5 * 60 * 1000

/**
 * Durée pendant laquelle un chargement réussi des sources du bâtiment (même id + même filtre)
 * évite un nouvel appel réseau (navigation entre pages du même bâtiment).
 */
const BUILDING_SOURCES_CACHE_TTL_MS = 60 * 1000 * 5

/**
 * Liste des `_id_source` à charger pour le bâtiment courant, selon le filtre optionnel.
 */
function getBuildingSourceIdList(
    buildingSources: Source[] | undefined,
    options?: { sourceTypes?: string[] }
): string[] {
    const sources = buildingSources ?? []
    if (options?.sourceTypes?.length) {
        return sources
            .filter((s: Source) => options.sourceTypes!.includes(s.idtype))
            .map((s: Source) => s._id_source)
    }
    return sources.map((s: Source) => s._id_source)
}

/**
 * True si chaque id demandé a déjà une entrée dans le store (évite un refetch réseau inutile).
 */
function areAllSourceIdsInStore(
    idList: string[],
    storedSources: Source[]
): boolean {
    if (idList.length === 0) {
        return true
    }
    const storedIds = new Set(storedSources.map((s) => s._id_source))
    return idList.every((id) => storedIds.has(id))
}

type BuildingSourcesCacheEntry = {
    buildingId: string
    idsFingerprint: string
    expiresAt: number
}

/**
 * Entrée du cache bâtiments (une page par clé companyId:page:limit)
 */
type BuildingsByCompanyCacheEntry = {
    data: Building[]
    total: number
    expiresAt: number
}

/** Limite par défaut pour une page (utilisée par fetchBuildingsByCompanyId pour tout charger) */
const DEFAULT_PAGE_LIMIT = 400

/** Construit la clé de cache pour une page */
function buildingsByCompanyCacheKey(
    companyId: string,
    page: number,
    limit: number
): string {
    return `${companyId}:${page}:${limit}`
}

/**
 * Indique si la réponse PUT bâtiment doit remplacer `building` du store (évite d’écraser
 * le bâtiment courant lors d’une mise à jour sur un autre id, ex. renommage depuis le dashboard).
 */
function shouldSyncUpdatedBuildingToCurrent(
    current: Building | null,
    urlBuildingId: string,
    updated: Building
): boolean {
    if (!current) return false
    if (
        urlBuildingId === current._id ||
        urlBuildingId === current._id_building
    ) {
        return true
    }
    if (updated._id && updated._id === current._id) return true
    if (updated._id_building && updated._id_building === current._id_building) {
        return true
    }
    return false
}

/** Paramètres passés au wrapper de mise à jour partielle bâtiment. */
type UpdateBuildingByIdKwargs = {
    buildingId: string
    payload: Record<string, unknown>
}

/**
 * Store permettant de gérer l'authentification de l'utilisateur au widget
 */
export const useBuildingStore = defineStore("buildingV2", () => {
    const sourceStore = useSourceStore()

    // -------------------------------------------------------------------------
    // State
    // -------------------------------------------------------------------------

    // --- Bâtiment (fiche) ---

    /**
     * Le bâtiment actuellement sélectionné
     */
    const building = ref<Building | null>(null)

    // --- Liste entreprise (cache paginé) ---

    /**
     * Cache des bâtiments par identifiant d'entreprise (companyId).
     * Chaque entrée expire après BUILDINGS_BY_COMPANY_CACHE_TTL_MS.
     */
    const buildingsByCompanyCache = ref<
        Record<string, BuildingsByCompanyCacheEntry>
    >({})

    /**
     * Dernier chargement réussi de sources (évite les refetch entre pages du même bâtiment).
     */
    const buildingSourcesCache = ref<BuildingSourcesCacheEntry | null>(null)

    /**
     * Déduplication des appels `fetchBuildingSources` en cours (même bâtiment + même jeu d'ids).
     */
    const fetchBuildingSourcesPromises = new Map<string, Promise<void>>()

    /**
     * Invalide le cache des sources du bâtiment (ex. avant navigation hors contexte).
     */
    function invalidateBuildingSourcesCache(): void {
        buildingSourcesCache.value = null
        fetchBuildingSourcesPromises.clear()
    }

    // -------------------------------------------------------------------------
    // Getters
    // -------------------------------------------------------------------------

    /**
     * Identifiant du bâtiment courant
     */
    const getBuildingId = computed(() => building.value?._id_building)

    // --- Sources & compteurs ---

    /**
     * Liste des sources du bâtiments en incluant les informations des sources du store source
     * et les informations spécifiques au bâtiment (consentement, consentrequest, etc.)
     */
    const getSources = computed<Source[]>(() => {
        return (
            sourceStore.sources
                .filter((source) =>
                    building.value?.sources
                        .map((s: Source) => s._id_source)
                        .includes(source._id_source)
                )
                ?.map((s) => {
                    const buildingSource = building.value?.sources.find(
                        (bs: Source) => bs._id_source === s._id_source
                    )
                    if (!buildingSource) return s

                    return {
                        ...s,
                        // Ajoutez ici d'autres propriétés si nécessaire

                        isUsed: buildingSource.isUsed,
                        consentement: buildingSource?.consentement,
                        consentrequest: buildingSource?.consentrequest,
                    }
                }) || []
        )
    })

    /**
     * Liste des compteurs du bâtiment (sources de type pdl, pce, cpcu, regaz, elecsavoie, dalkia, eaubordeaux)
     */
    const getMeters = computed(() => {
        return getSources.value.filter((source) =>
            METER_TYPES_LIST.includes(source.type)
        )
    })

    // --- Bâtiment (fiche) ---

    /**
     * Indique si l'utilisateur a le droit "owns" sur le bâtiment (propriétaire)
     * Utilisé pour restreindre l'édition de la station météo aux seuls propriétaires
     */
    const getBuildingHasOwns = computed(() => {
        const rights = building.value?.rights
        if (!Array.isArray(rights)) return false
        return rights.includes("owns")
    })

    /**
     * Accès « partagé » : uniquement le droit `views` (lien lecture seule).
     * Dans ce cas, la fiche bâtiment est consultable mais les mutations sont désactivées côté UI.
     */
    const getBuildingIsViewOnly = computed(() => {
        const rights = building.value?.rights
        return (
            Array.isArray(rights) &&
            rights.length > 0 &&
            rights.every((right) => right === "views")
        )
    })

    // -------------------------------------------------------------------------
    // Méthodes
    // -------------------------------------------------------------------------

    /**
     * Injection des services
     */
    const services = inject<Services>("services")

    // --- Bâtiment (fiche) ---

    /**
     * Met à jour le profil d'utilisation du bâtiment
     * @param profile - Profil d'utilisation à mettre à jour
     */
    function updateBuildingUsageProfile(profile: UsageProfile) {
        if (!building.value) return
        building.value.profil_utilisation = {
            _id_profilutilisation: profile,
        }
    }

    /**
     * Retire le profil d'utilisation du bâtiment en cours (sans refetch API).
     */
    function removeBuildingUsageProfile() {
        if (!building.value) return
        building.value.profil_utilisation = undefined
    }

    /**
     * Récupère les informations d'un bâtiment par son identifiant
     */
    const {
        fetch: fetchBuildingRaw,
        isFetching: isFetchingBuilding,
        hasError: hasErrorBuilding,
        error: errorBuilding,
    } = useFetchWrapper(
        async (
            {
                _id_building,
                options = { children: false },
            }: {
                _id_building: string
                options?: { children?: boolean }
            },
            controller?: AbortController
        ) => {
            if (!services?.$getBuildingById) {
                throw new Error("Service $getBuildingById non disponible")
            }

            try {
                const previousBuildingId = building.value?._id_building
                building.value = await services.$getBuildingById(
                    _id_building,
                    { children: options?.children },
                    controller
                )
                if (
                    previousBuildingId !== undefined &&
                    previousBuildingId !== _id_building
                ) {
                    sourceStore.$reset()
                    invalidateBuildingSourcesCache()
                }
            } catch (error) {
                console.error(
                    "Erreur lors de la récupération du bâtiment:",
                    error
                )
                throw error
            }
        }
    )

    /**
     * Promesses de fetch building en cours par identifiant (déduplication des appels concurrents).
     */
    const fetchBuildingPromiseById: Partial<Record<string, Promise<void>>> = {}

    /**
     * Récupère les informations d'un bâtiment par son identifiant (déduplique les appels simultanés pour le même id).
     * Si un fetch est déjà en cours pour ce bâtiment, retourne la même promesse.
     * @param options.silent - Ne pas activer isFetchingBuilding (ex. après ajout d'un compteur).
     */
    async function fetchBuilding(
        _id_building: string,
        options: { silent?: boolean; children?: boolean } = {
            silent: false,
            children: true,
        }
    ): Promise<void> {
        // Si l'option children n'est pas présent dans le "options" alors on ajoute les enfants par défaut
        if (options.children == null) {
            options.children = true
        }

        const existing = fetchBuildingPromiseById[_id_building]
        if (existing) {
            return existing
        }
        const promise = fetchBuildingRaw({
            _id_building,
            options,
        }).finally(() => {
            delete fetchBuildingPromiseById[_id_building]
        })
        fetchBuildingPromiseById[_id_building] = promise
        return promise
    }

    /**
     * PUT /api/metier/building/:id — mise à jour partielle ; met à jour `building` si c’est le bâtiment courant.
     */
    const {
        fetch: updateBuildingByIdRaw,
        isFetching: isUpdatingBuildingById,
        hasError: hasErrorUpdatingBuildingById,
        error: errorUpdatingBuildingById,
    } = useFetchWrapper(
        async (
            { buildingId, payload }: UpdateBuildingByIdKwargs,
            controller
        ) => {
            if (!services?.$updateBuildingById) {
                throw new Error("Service $updateBuildingById non disponible")
            }
            try {
                const updated = await services.$updateBuildingById(
                    buildingId,
                    payload,
                    controller
                )
                if (
                    shouldSyncUpdatedBuildingToCurrent(
                        building.value,
                        buildingId,
                        updated
                    )
                ) {
                    building.value = updated
                }
            } catch (err) {
                console.error("Erreur lors de la mise à jour du bâtiment:", err)
                throw err
            }
        }
    )

    /**
     * Met à jour un bâtiment par identifiant d’URL (Mongo ou métier, selon l’appelant).
     * @param buildingId - Identifiant utilisé dans PUT /api/metier/building/:id
     * @param payload - Corps JSON (parties, infos, name, etc.)
     * @param options.silent - Ne pas activer isUpdatingBuildingById (ex. formulaires inline).
     */
    async function updateBuildingById(
        buildingId: string,
        payload: Record<string, unknown>,
        options?: { silent?: boolean }
    ): Promise<void> {
        return updateBuildingByIdRaw(
            { buildingId, payload },
            { silent: options?.silent }
        )
    }

    /**
     * DELETE /v5/building/:id/children — retire des enfants du parent.
     * La réponse est le bâtiment parent rechargé ; si le bâtiment courant est un enfant
     * détaché, on met à jour `_id_parent` localement sans refetch.
     */
    async function removeBuildingChildren(
        parentId: string,
        childIds: string[]
    ): Promise<void> {
        if (!services?.$removeBuildingChildren) {
            throw new Error("Service $removeBuildingChildren non disponible")
        }
        const updated = await services.$removeBuildingChildren(
            parentId,
            childIds
        )
        const current = building.value
        if (!current) return

        const viewingParent =
            parentId === current._id || parentId === current._id_building

        if (viewingParent) {
            building.value = updated
            return
        }

        const detachedSelf = childIds.some(
            (id) => id === current._id || id === current._id_building
        )

        if (detachedSelf) {
            building.value = {
                ...current,
                _id_parent: null,
            } as unknown as Building
        }
    }

    /**
     * POST /v5/building/:id/children — rattache des enfants au parent.
     * La réponse est le parent rechargé (avec enfants) ; si le bâtiment courant est un enfant
     * nouvellement lié, on met à jour `_id_parent` (et le niveau immeuble / maison_immeuble) localement.
     */
    async function addBuildingChildren(
        parentId: string,
        childrenIds: string[]
    ): Promise<void> {
        if (!services?.$addBuildingChildren) {
            throw new Error("Service $addBuildingChildren non disponible")
        }
        const updatedParent = await services.$addBuildingChildren(
            parentId,
            childrenIds
        )
        const current = building.value
        if (!current) return

        const viewingParent =
            parentId === current._id || parentId === current._id_building

        if (viewingParent) {
            building.value = updatedParent
            return
        }

        const linkedAsChild = childrenIds.some(
            (id) => id === current._id || id === current._id_building
        )

        if (!linkedAsChild) return

        const pn = updatedParent.infos?.niveau
        const niveauOverride =
            pn === "immeuble"
                ? "appartement"
                : pn === "maison_immeuble"
                  ? "maison_appartement"
                  : undefined

        building.value = {
            ...current,
            _id_parent: updatedParent._id ?? parentId,
            infos:
                current.infos && niveauOverride
                    ? { ...current.infos, niveau: niveauOverride }
                    : current.infos,
        } as unknown as Building
    }

    /**
     * PUT /api/metier/building/:id/validation — valide la parcelle cadastrale.
     * Met à jour `building` avec la réponse sans refetch.
     */
    async function validateBuildingParcel(
        buildingId: string,
        parcelId: string,
        parcelLocation?: ParcelLocationMultiPolygon
    ): Promise<void> {
        if (!services?.$validateBuildingParcel) {
            throw new Error("Service $validateBuildingParcel non disponible")
        }
        const updated = await services.$validateBuildingParcel(
            buildingId,
            parcelId,
            parcelLocation
        )
        if (
            shouldSyncUpdatedBuildingToCurrent(
                building.value,
                buildingId,
                updated
            )
        ) {
            building.value = updated
        }
    }

    // --- Liste entreprise ---

    /**
     * Récupère une page de bâtiments d'une entreprise (avec cache 5 min).
     * @param companyId - Identifiant de l'entreprise
     * @param options - page (1-based) et limit
     * @returns Liste des bâtiments de la page et total
     */
    async function fetchBuildingsByCompanyIdPage(
        companyId: string,
        options: { page: number; limit: number }
    ): Promise<{ data: Building[]; total: number }> {
        const { page, limit } = options
        const now = Date.now()
        const key = buildingsByCompanyCacheKey(companyId, page, limit)
        const entry = buildingsByCompanyCache.value[key]
        if (entry && entry.expiresAt > now) {
            return { data: entry.data, total: entry.total }
        }
        if (!services?.$getBuildingsByCompanyIdPage) {
            throw new Error(
                "Service $getBuildingsByCompanyIdPage non disponible"
            )
        }
        const result = await services.$getBuildingsByCompanyIdPage(
            companyId,
            page,
            limit
        )
        const data = (result?.data ?? []) as Building[]
        const total = result?.total ?? 0
        buildingsByCompanyCache.value[key] = {
            data,
            total,
            expiresAt: now + BUILDINGS_BY_COMPANY_CACHE_TTL_MS,
        }
        return { data, total }
    }

    /**
     * Récupère tous les bâtiments d'une entreprise (pages chargées une à une, chaque page mise en cache).
     * @param companyId - Identifiant de l'entreprise
     * @returns Liste complète des bâtiments
     */
    async function fetchBuildingsByCompanyId(
        companyId: string
    ): Promise<Building[]> {
        const results: Building[] = []
        let page = 1
        let total = 0
        do {
            const { data, total: totalCount } =
                await fetchBuildingsByCompanyIdPage(companyId, {
                    page,
                    limit: DEFAULT_PAGE_LIMIT,
                })
            if (page === 1) {
                total = totalCount
            }
            results.push(...data)
            if (results.length >= total) {
                break
            }
            page++
        } while (results.length < total)
        return results
    }

    /**
     * Invalide le cache des bâtiments par entreprise.
     * @param companyId - Si fourni, invalide toutes les pages de ce companyId ; sinon invalide tout le cache.
     */
    function invalidateBuildingsByCompanyCache(companyId?: string): void {
        if (companyId !== undefined) {
            const prefix = `${companyId}:`
            const next = { ...buildingsByCompanyCache.value }
            for (const key of Object.keys(next)) {
                if (key.startsWith(prefix)) {
                    delete next[key]
                }
            }
            buildingsByCompanyCache.value = next
        } else {
            buildingsByCompanyCache.value = {}
        }
    }

    // --- Sources & compteurs ---

    /**
     * Met à jour la station météo de référence du bâtiment puis met à jour le store
     * @param _id_building - Identifiant du bâtiment
     * @param newSourceId - Identifiant de la nouvelle station météo
     * @returns Le bâtiment mis à jour
     */
    const {
        fetch: updateBuildingWeatherStation,
        isFetching: isUpdatingBuildingWeatherStation,
        hasError: hasErrorUpdatingBuildingWeatherStation,
        error: errorUpdatingBuildingWeatherStation,
    } = useFetchWrapper(async ({ _id_building, newSourceId }, controller) => {
        if (!services?.$updateBuildingWeatherStation) {
            throw new Error(
                "Service $updateBuildingWeatherStation non disponible"
            )
        }

        try {
            building.value = await services.$updateBuildingWeatherStation(
                _id_building,
                newSourceId,
                controller
            )
            invalidateBuildingSourcesCache()
        } catch (error) {
            console.error(
                "Erreur lors de la mise à jour de la station météo:",
                error
            )
            throw error
        }
    })

    /**
     * Retourne les noms de variables disponibles pour une source (d'après monitoring.lastdate).
     * @param _id_source - Identifiant de la source
     * @returns Liste des noms de variables (clés lastdate avec au moins une fréquence daily/hourly/weekly/monthly)
     */
    function getSourceVariables(_id_source: string): string[] {
        const source = getSources.value.find((s) => s._id_source === _id_source)
        const lastdate = source?.monitoring?.lastdate
        if (!lastdate) return []
        const freqKeys = ["daily", "hourly", "weekly", "monthly"] as const
        return Object.entries(lastdate)
            .filter(
                ([key, value]) =>
                    !["min_freq", "max_freq"].includes(key) &&
                    value &&
                    typeof value === "object" &&
                    freqKeys.some((f) => (value as Record<string, unknown>)[f])
            )
            .map(([key]) => key)
    }

    const {
        fetch: fetchBuildingSourcesRaw,
        isFetching: isFetchingBuildingSources,
        hasError: hasErrorBuildingSources,
        error: errorBuildingSources,
    } = useFetchWrapper(async (options?: { sourceTypes?: string[] }) => {
        const idList = getBuildingSourceIdList(building.value?.sources, options)
        /** Filtre par types : enrichit le store sans écraser les autres sources (ex. compteurs + capteurs). */
        const isPartialTypeFetch = Boolean(options?.sourceTypes?.length)
        await sourceStore.fetchSources({
            _id_sources: idList,
            reset: !isPartialTypeFetch,
        })
    })

    /**
     * Recharge les sources du bâtiment dans le store source.
     * Saute l’appel réseau si le même jeu de sources vient d’être chargé (TTL), sauf si `force`.
     * @param options.silent - Ne pas activer isFetchingBuildingSources.
     * @param options.force - Ignorer le cache (après mutation, reload explicite, etc.).
     */
    async function fetchBuildingSources(options?: {
        sourceTypes?: string[]
        silent?: boolean
        force?: boolean
    }): Promise<void> {
        const { force, silent, ...sourceOptions } = options ?? {}
        const buildingId = building.value?._id_building
        const idList = getBuildingSourceIdList(
            building.value?.sources,
            sourceOptions
        )

        if (!buildingId) {
            return fetchBuildingSourcesRaw(sourceOptions, { silent })
        }

        const fingerprint = [...idList].sort().join(",")
        const dedupeKey = `${buildingId}:${fingerprint}`

        if (!force) {
            /** Déjà en mémoire : pas d’appel (indépendant du cache TTL, utile après un chargement « toutes les sources »). */
            if (areAllSourceIdsInStore(idList, sourceStore.sources)) {
                if (idList.length > 0) {
                    buildingSourcesCache.value = {
                        buildingId,
                        idsFingerprint: fingerprint,
                        expiresAt: Date.now() + BUILDING_SOURCES_CACHE_TTL_MS,
                    }
                }
                return Promise.resolve()
            }

            const cached = buildingSourcesCache.value
            if (
                cached &&
                cached.buildingId === buildingId &&
                cached.idsFingerprint === fingerprint &&
                Date.now() < cached.expiresAt
            ) {
                return Promise.resolve()
            }
        }

        const pendingFetch = fetchBuildingSourcesPromises.get(dedupeKey)
        if (pendingFetch) {
            return pendingFetch
        }

        const run = (async () => {
            try {
                await fetchBuildingSourcesRaw(sourceOptions, { silent })
                if (idList.length > 0) {
                    buildingSourcesCache.value = {
                        buildingId,
                        idsFingerprint: fingerprint,
                        expiresAt: Date.now() + BUILDING_SOURCES_CACHE_TTL_MS,
                    }
                }
            } finally {
                fetchBuildingSourcesPromises.delete(dedupeKey)
            }
        })()

        fetchBuildingSourcesPromises.set(dedupeKey, run)
        return run
    }

    /**
     * Active ou désactive une source du bâtiment (isUsed) puis rafraîchit le bâtiment
     * @param _id_building - Identifiant du bâtiment
     * @param sourceId - Identifiant de la source
     * @param isUsed - Indique si la source est active ou non
     * @returns Le bâtiment mis à jour
     */
    const {
        fetch: updateBuildingSourceUsed,
        isFetching: isUpdatingBuildingSourceUsed,
        hasError: hasErrorUpdatingBuildingSourceUsed,
        error: errorUpdatingBuildingSourceUsed,
    } = useFetchWrapper(
        async ({ _id_building, sourceId, isUsed }, controller) => {
            if (
                !services?.$updateBuildingSourceUse ||
                !services?.$updateBuildingSourceUnuse
            ) {
                throw new Error(
                    "Services updateBuildingSourceUse/Unuse non disponibles"
                )
            }
            try {
                if (isUsed) {
                    await services.$updateBuildingSourceUse(
                        _id_building,
                        sourceId,
                        controller
                    )
                } else {
                    await services.$updateBuildingSourceUnuse(
                        _id_building,
                        sourceId,
                        controller
                    )
                }

                // Met à jour la valeur dans le champ building.sources pour la source concernée
                const sourceIndex = building.value?.sources.findIndex(
                    (s: Source) => s._id_source === sourceId
                )
                if (sourceIndex !== -1) {
                    // Mise à jour réactif avec splice pour la réactivité
                    building.value?.sources.splice(sourceIndex, 1, {
                        ...building.value?.sources[sourceIndex],
                        isUsed,
                    })
                }
            } catch (error) {
                console.error(
                    "Erreur lors de la mise à jour de la source:",
                    error
                )
                throw error
            }
        }
    )

    // --- Cycle de vie du store ---

    /**
     * Réinitialise le store à son état initial.
     */
    function $reset() {
        building.value = null
        buildingsByCompanyCache.value = {}
        invalidateBuildingSourcesCache()
    }

    return {
        // State
        building,
        buildingsByCompanyCache,

        // Getters
        getBuildingId,
        getSources,
        getMeters,
        getBuildingHasOwns,
        getBuildingIsViewOnly,

        // Méthodes — Bâtiment (fiche)
        updateBuildingUsageProfile,
        removeBuildingUsageProfile,
        fetchBuilding,
        updateBuildingById,
        removeBuildingChildren,
        addBuildingChildren,
        validateBuildingParcel,

        // Méthodes — Liste entreprise
        fetchBuildingsByCompanyId,
        fetchBuildingsByCompanyIdPage,
        invalidateBuildingsByCompanyCache,
        invalidateBuildingSourcesCache,

        // Méthodes — Sources & compteurs
        updateBuildingWeatherStation,
        getSourceVariables,
        fetchBuildingSources,
        updateBuildingSourceUsed,

        // Méthodes — Cycle de vie
        $reset,

        // États de chargement
        isFetchingBuilding,
        isUpdatingBuildingById,
        isFetchingBuildingSources,
        isUpdatingBuildingSourceUsed,
        isUpdatingBuildingWeatherStation,

        // États d'erreur
        hasErrorBuilding,
        hasErrorUpdatingBuildingById,
        hasErrorBuildingSources,
        hasErrorUpdatingBuildingSourceUsed,
        hasErrorUpdatingBuildingWeatherStation,

        // Erreurs
        errorBuilding,
        errorUpdatingBuildingById,
        errorBuildingSources,
        errorUpdatingBuildingSourceUsed,
        errorUpdatingBuildingWeatherStation,
    }
})
